import { useState } from "react";
import { PageHeader } from "@/components/base/ui";
import { sendContactEmail, isEmailJSConfigured, getEmailTo, getConfigError } from "@/config/emailjs";

export default function Contact() {
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const configError = getConfigError();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState("submitting");
    setErrorMessage("");

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const params: Record<string, unknown> = {
      to_email: getEmailTo(),
      from_name: formData.get("full_name"),
      company: formData.get("company") || "Not provided",
      email: formData.get("email"),
      phone: formData.get("phone") || "Not provided",
      message: formData.get("message"),
      page: "Contact Page",
    };

    try {
      if (!isEmailJSConfigured()) {
        throw new Error(configError || "EmailJS is not configured.");
      }
      await sendContactEmail(params);
      setFormState("success");
      form.reset();
    } catch (err) {
      setFormState("error");
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  return (
    <div>
      <PageHeader
        title="Contact Us"
        subtitle="Get in touch for quotations, partnerships, or any questions about our stone products and services."
      />

      {/* Contact Info + Form */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 md:gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <span className="text-gold-500 text-xs font-sans uppercase tracking-[0.2em] mb-4 block">
                Get in Touch
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 leading-tight mb-6">
                Let's Discuss Your Project
              </h2>
              <p className="text-stone-500 text-base font-sans leading-relaxed mb-8">
                Our team is ready to assist with product inquiries, quotations, shipping logistics, and partnership opportunities.
              </p>

              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg border border-stone-200 flex-shrink-0">
                    <i className="ri-phone-line text-gold-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-serif text-sm font-semibold text-stone-900 mb-1">Phone</p>
                    <a href="tel:+352691203341" className="text-stone-500 text-sm font-sans hover:text-gold-500 transition-colors">
                      +352 691 203 341
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg border border-stone-200 flex-shrink-0">
                    <i className="ri-mail-line text-gold-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-serif text-sm font-semibold text-stone-900 mb-1">Email</p>
                    <a href="mailto:contact@lithos.pro" className="text-stone-500 text-sm font-sans hover:text-gold-500 transition-colors">
                      contact@lithos.pro
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg border border-stone-200 flex-shrink-0">
                    <i className="ri-whatsapp-line text-gold-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-serif text-sm font-semibold text-stone-900 mb-1">WhatsApp</p>
                    <a href="https://wa.me/352691203341" target="_blank" rel="noopener noreferrer" className="text-stone-500 text-sm font-sans hover:text-gold-500 transition-colors">
                      +352 691 203 341
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg border border-stone-200 flex-shrink-0">
                    <i className="ri-map-pin-line text-gold-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-serif text-sm font-semibold text-stone-900 mb-1">Location</p>
                    <p className="text-stone-500 text-sm font-sans">France</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg border border-stone-200 flex-shrink-0">
                    <i className="ri-global-line text-gold-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-serif text-sm font-semibold text-stone-900 mb-1">Website</p>
                    <a href="https://lithos.pro" target="_blank" rel="noopener noreferrer" className="text-stone-500 text-sm font-sans hover:text-gold-500 transition-colors">
                      lithos.pro
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex gap-3 mt-8">
                <a
                  href="#"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-stone-200 rounded-md text-stone-500 hover:text-gold-500 hover:border-gold-500 transition-colors"
                >
                  <i className="ri-linkedin-fill text-sm"></i>
                </a>
                <a
                  href="#"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-stone-200 rounded-md text-stone-500 hover:text-gold-500 hover:border-gold-500 transition-colors"
                >
                  <i className="ri-instagram-line text-sm"></i>
                </a>
                <a
                  href="https://wa.me/352691203341"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-stone-200 rounded-md text-stone-500 hover:text-gold-500 hover:border-gold-500 transition-colors"
                >
                  <i className="ri-whatsapp-line text-sm"></i>
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg p-6 md:p-8 border border-stone-200">
                <h3 className="font-serif text-xl font-semibold text-stone-900 mb-6">
                  Send us a Message
                </h3>

                {formState === "success" && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <i className="ri-check-line text-green-500 text-lg"></i>
                      <p className="text-green-700 text-sm font-sans">
                        Thank you! Your message has been sent. We will get back to you within 24 hours.
                      </p>
                    </div>
                  </div>
                )}

                {formState === "error" && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <i className="ri-error-warning-line text-red-500 text-lg"></i>
                      <p className="text-red-700 text-sm font-sans">
                        {errorMessage || "Something went wrong. Please try again or contact us directly."}
                      </p>
                    </div>
                  </div>
                )}

                {configError && (
                  <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <i className="ri-settings-3-line text-amber-500 text-lg mt-0.5"></i>
                      <div>
                        <p className="text-amber-700 text-sm font-sans font-medium mb-1">
                          Email not configured yet
                        </p>
                        <p className="text-amber-600 text-xs font-sans">
                          {configError} Add them to your .env file or use the form below and we will contact you via WhatsApp or phone.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors"
                        placeholder="Your company (optional)"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors"
                      placeholder="+352 691 203 341"
                    />
                  </div>

                  <div>
                    <label className="block text-stone-700 text-sm font-sans font-medium mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-stone-200 rounded-md text-sm font-sans text-stone-700 focus:outline-none focus:border-gold-500 transition-colors resize-none"
                      placeholder="Tell us about your project, the stone you're interested in, and your requirements..."
                    ></textarea>
                    <p className="text-stone-400 text-xs font-sans mt-1">Maximum 500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={formState === "submitting"}
                    className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-stone-900 text-white font-sans font-medium text-sm rounded-md hover:bg-stone-800 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {formState === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-0 bg-white">
        <div className="w-full h-[400px] md:h-[500px]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.991625693761!2d2.2922926!3d48.8583701!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e2964e34e2d%3A0x8ddca9ee380ef7e0!2sEiffel%20Tower!5e0!3m2!1sen!2sfr!4v1700000000000!5m2!1sen!2sfr"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Lithos Location"
          ></iframe>
        </div>
      </section>
    </div>
  );
}